import type { AxiosRequestConfig } from "axios";
import { ytgApi } from "./client";
import { ytgEndpoints } from "./endpoints";
import { API_ENDPOINTS } from "@config/api";
import type { Client, Language, Operation, SerpItem } from "@shared-types";
import type { components } from "@shared-types/api-ytg.gen";

// ---------- Sub-process response shapes ----------

/** Status payload of the operation-level PDF generation sub-process. */
export type PdfGenerationStatus = components["schemas"]["PdfGenerationStatusResponse"];

/**
 * Aggregate PDF status overview for an operation (`/pdf_overview`).
 *
 * Hand-written: the route is proxied by the gateway with an untyped (`object`)
 * passthrough schema and has no typed model on either backend, so there is no
 * generated equivalent to alias.
 */
export interface PdfOverview {
    total: number;
    by_status: Record<string, number>;
    [key: string]: unknown;
}

// The Drive/Graph upload start/status routes below are gateway generic proxies
// (untyped `object` schema, no typed YTG model) — kept hand-written for the
// same reason as PdfOverview above.
/** Response returned when starting a Drive PDF upload (holds the Celery task id). */
export interface DriveUploadStartResponse {
    status: string;
    celery_task_id: string;
    [key: string]: unknown;
}

/** Progress snapshot of an in-flight Drive PDF upload task. */
export interface DriveUploadStatus {
    status: string;
    progress: number;
    total: number;
    message?: string;
    [key: string]: unknown;
}

/**
 * Response returned when starting a Drive graph upload (holds the task id).
 *
 * Carries no count: the route no longer loads the operation's rows to produce one, and
 * the authoritative total — the number of graphs actually generated — arrives with the
 * `drive.upload_started` WebSocket event.
 */
export interface GraphUploadStartResponse {
    status: string;
    task_id: string;
    [key: string]: unknown;
}

/** Progress snapshot of an in-flight Drive graph upload task. */
export interface GraphUploadStatus {
    status: string;
    uploaded: number;
    total: number;
    failed: number;
    [key: string]: unknown;
}

/** Per-operation KPI stats returned by `/operation/:id/stats`. */
export type OperationStatsResponse = components["schemas"]["OperationKpiStats"];

/** Per-row task status (admin grid). ``tasks`` is keyed by task name. */
export type AdminRowStatus = components["schemas"]["RowStatusItem"];

/** Paginated per-row task status grid returned by the admin rows-status endpoint. */
export type AdminRowsStatusResponse = components["schemas"]["RowsStatusResponse"];

/** Column the admin rows-status grid can be sorted by (a task name sorts by its status). */
export type RowsSortBy =
    | "row_id" | "keyword" | "error_count"
    | "gen_guide" | "get_guide" | "get_content_score" | "get_serp_guide" | "get_competitors_score";

/** Derived per-(row, task) status the grid can filter on. */
export type RowStatusFilter = "pending" | "completed" | "failed";

/** Query parameters accepted by the admin rows-status endpoint. */
export interface AdminRowsStatusParams {
    skip?: number;
    limit?: number;
    sort_by?: RowsSortBy;
    sort_dir?: "asc" | "desc";
    filter_task?: RestartableTask;
    filter_status?: RowStatusFilter;
}

/**
 * One website row's extraction trace, from the admin extraction endpoint (no page content).
 * Hand-written (not generated) so the front does not depend on a Docker `gen:api-types` run.
 */
export interface ExtractionRowItem {
    row_id: number;
    keyword: string;
    website_url: string | null;
    domain: string | null;
    extraction_method: string | null;
    extraction_outcome: string | null;
    extraction_http_status: number | null;
    extraction_fallback_reason: string | null;
}

/** Aggregate counts of the extraction outcomes over an operation's website rows. */
export interface ExtractionSummary {
    total: number;
    ytg_profile: number;
    trafilatura_rendered: number;
    trafilatura_classic: number;
    blocked: number;
    pending: number;
}

/** Response of the admin per-operation extraction endpoint: summary + paginated per-row trace. */
export interface OperationExtractionResponse {
    summary: ExtractionSummary;
    items: ExtractionRowItem[];
    total: number;
    skip: number;
    limit: number;
    has_more: boolean;
}

/** Query parameters accepted by the admin extraction endpoint. */
export interface OperationExtractionParams {
    skip?: number;
    limit?: number;
    filter_method?: string;
    filter_reason?: string;
}

/** The ProcessStatus values the admin status-override endpoints accept. */
export type ProcessStatusValue =
    | "pending" | "in_progress" | "completed" | "failed" | "ignored" | "blocked";

/** All ProcessStatus values, for building the override dropdowns. */
export const PROCESS_STATUS_VALUES: readonly ProcessStatusValue[] = [
    "pending", "in_progress", "completed", "failed", "ignored", "blocked",
] as const;

/** Tasks that can be restarted per-row via the admin by-row-ids endpoint. */
export type RestartableTask =
    | "gen_guide"
    | "get_guide"
    | "get_content_score"
    | "get_serp_guide"
    | "get_competitors_score";

/** Response echoing the chained tasks after an admin restart-by-row-ids call. */
export type AdminRestartByRowIdsResponse = components["schemas"]["RestartByRowIdsResponse"];

/** Paginated list of operation rows. */
export type RowsListResponse = components["schemas"]["PaginatedRowsResponse"];

/** Score payload returned by `/row/:id/score`. */
export type RowScoreResponse = components["schemas"]["ContentScore"];

/**
 * Request timeout of the operation PDF ZIP download (milliseconds). The gateway
 * and nginx give up after 300 s; 10 s more lets the gateway's 504
 * `YTG_ZIP_TIMEOUT` reach the browser instead of a bare axios timeout. The YTG
 * client keeps 30 s for every other call.
 */
export const PDF_ZIP_TIMEOUT_MS = 310_000;

// Absolute-URL helper for endpoints used outside of axios (e.g. as <a href>).
// The axios client has baseURL set, so `ytgApi.get(ytgEndpoints.xyz())` works
// fine with the relative path, but anchors / window.open need the full path.
const absoluteYtgUrl = (relativePath: string): string =>
    `${API_ENDPOINTS.YTG}${relativePath}`;

/**
 * Thin data-access layer over the YTG API: each method wraps an axios call and
 * resolves to the unwrapped response body (or, for downloads, builds an
 * absolute URL usable in `<a href>` / `window.open`).
 *
 * @example
 * ```ts
 * const rows = await YtgService.getOperationRows(operationId);
 * const status = await YtgService.getPdfGenerationStatus(operationId);
 * const zipUrl = YtgService.downloadOpZipUrl(operationId); // for <a href>
 * ```
 */
export const YtgService = {
    // --- reads ---
    /** Fetch the list of clients. */
    getClients: (config?: AxiosRequestConfig): Promise<Client[]> =>
        ytgApi.get<Client[]>(ytgEndpoints.client(), config).then((r) => r.data),
    /** Fetch the list of supported languages. */
    getLanguages: (config?: AxiosRequestConfig): Promise<Language[]> =>
        ytgApi.get<Language[]>(ytgEndpoints.lang(), config).then((r) => r.data),
    /** Fetch all operations. */
    getOperations: (config?: AxiosRequestConfig): Promise<Operation[]> =>
        ytgApi.get<Operation[]>(ytgEndpoints.operation(), config).then((r) => r.data),
    /** Fetch a single operation by id (rows excluded). */
    getOperationById: (id: number | string, config?: AxiosRequestConfig): Promise<Operation> =>
        ytgApi
            .get<Operation>(ytgEndpoints.operationById(id), {
                params: { include_rows: false },
                ...config,
            })
            .then((r) => r.data),
    /**
     * Fetch a paginated page of an operation's rows.
     *
     * @param params - Pagination/filter query params forwarded as-is.
     */
    getOperationRows: (
        opId: number | string,
        params?: Record<string, unknown>,
    ): Promise<RowsListResponse> =>
        ytgApi
            .get<RowsListResponse>(ytgEndpoints.operationRows(opId), { params })
            .then((r) => r.data),
    /** Fetch the KPI stats of an operation. */
    getOperationStats: (opId: number | string): Promise<OperationStatsResponse> =>
        ytgApi.get<OperationStatsResponse>(ytgEndpoints.operationStats(opId)).then((r) => r.data),
    /** Fetch the SERP results of a row. */
    getSerpByRowId: (id: number | string, config?: AxiosRequestConfig): Promise<SerpItem[]> =>
        ytgApi.get<SerpItem[]>(ytgEndpoints.serpByRowId(id), config).then((r) => r.data),
    /** Fetch the content score of a row. */
    getRowScore: (id: number | string, config?: AxiosRequestConfig): Promise<RowScoreResponse> =>
        ytgApi
            .get<RowScoreResponse>(`${ytgEndpoints.rowById(id)}/score`, config)
            .then((r) => r.data),
    /** Fetch the guide date/keyword metadata of a row. */
    getRowDate: (id: number | string, config?: AxiosRequestConfig): Promise<unknown> =>
        ytgApi.get<unknown>(`${ytgEndpoints.rowById(id)}/date`, config).then((r) => r.data),
    /** Submit ad-hoc content to be scored against a row's guide. */
    postAdhocScore: (rowId: number | string, content: string): Promise<unknown> =>
        ytgApi
            .post<unknown>(ytgEndpoints.rowAdhocScore(rowId), { content })
            .then((r) => r.data),
    /** Fetch the top-ranking domains across an operation's SERPs. */
    getTopDomains: (opId: number | string): Promise<unknown> =>
        ytgApi.get<unknown>(ytgEndpoints.topDomains(opId)).then((r) => r.data),

    // --- downloads (raw URLs, to put on <a href=... download) ---
    /** Build the absolute URL to download a row's PDF. */
    downloadRowPdfUrl: (rowId: number | string): string =>
        absoluteYtgUrl(ytgEndpoints.rowPdf(rowId)),
    /** Build the absolute URL to download an operation's PDF ZIP archive. */
    downloadOpZipUrl: (opId: number | string): string =>
        absoluteYtgUrl(ytgEndpoints.opZip(opId)),

    /** Build the absolute URL to the inline HTML preview of a row's guide. */
    getRowViewUrl: (rowId: number | string): string =>
        absoluteYtgUrl(ytgEndpoints.rowView(rowId)),

    // ========== PDF GENERATION ==========

    /** Start the PDF generation sub-process for an operation. */
    startPdfGeneration: (operationId: number | string): Promise<{ status: string }> =>
        ytgApi
            .post<{ status: string }>(ytgEndpoints.generatePdfsStart(operationId))
            .then((r) => r.data),

    /** Poll the PDF generation status of an operation. */
    getPdfGenerationStatus: (operationId: number | string): Promise<PdfGenerationStatus> =>
        ytgApi
            .get<PdfGenerationStatus>(ytgEndpoints.generatePdfsStatus(operationId))
            .then((r) => r.data),

    /**
     * Download the PDF ZIP as a Blob (streaming response).
     *
     * Uses its own {@link PDF_ZIP_TIMEOUT_MS} instead of the client's 30 s: when
     * guide PDFs are missing from storage, YTG rebuilds them on the fly and the
     * archive can take several minutes for a few dozen guides.
     */
    downloadPdfZip: (operationId: number | string): Promise<Blob> =>
        ytgApi
            .get<Blob>(ytgEndpoints.opZip(operationId), {
                responseType: "blob",
                timeout: PDF_ZIP_TIMEOUT_MS,
            })
            .then((r) => r.data),

    /**
     * Download a row's PDF as a Blob, with the raw `Content-Disposition` header
     * so the caller can keep the server's file name.
     */
    downloadRowPdf: (rowId: number | string): Promise<{ blob: Blob; contentDisposition?: string }> =>
        ytgApi
            .get<Blob>(ytgEndpoints.rowPdf(rowId), { responseType: "blob" })
            .then((r) => {
                const header: unknown = r.headers?.["content-disposition"];
                return {
                    blob: r.data,
                    contentDisposition: typeof header === "string" ? header : undefined,
                };
            }),

    /** Fetch the aggregate PDF status overview of an operation. */
    getPdfOverview: (operationId: number | string): Promise<PdfOverview> =>
        ytgApi.get<PdfOverview>(ytgEndpoints.pdfOverview(operationId)).then((r) => r.data),

    // ========== GOOGLE DRIVE ==========

    /**
     * Start uploading an operation's PDFs to a Google Drive folder.
     *
     * @param folderId - Destination Google Drive folder id.
     */
    startDriveUpload: (
        operationId: number | string,
        folderId: string,
    ): Promise<DriveUploadStartResponse> =>
        ytgApi
            .post<DriveUploadStartResponse>(ytgEndpoints.uploadDriveStart(operationId), {
                folder_id: folderId,
            })
            .then((r) => r.data),

    /** Poll a Drive PDF upload task by its id. */
    getDriveUploadStatus: (
        operationId: number | string,
        taskId: string,
    ): Promise<DriveUploadStatus> =>
        ytgApi
            .get<DriveUploadStatus>(ytgEndpoints.uploadDriveStatus(operationId, taskId))
            .then((r) => r.data),

    // ========== GRAPH UPLOAD ==========

    /**
     * Start uploading an operation's graphs to a Google Drive folder.
     *
     * @param folderId - Destination Google Drive folder id.
     */
    startGraphUpload: (
        operationId: number | string,
        folderId: string,
    ): Promise<GraphUploadStartResponse> =>
        ytgApi
            .post<GraphUploadStartResponse>(ytgEndpoints.uploadGraphsStart(operationId), null, {
                params: { target_folder_id: folderId },
            })
            .then((r) => r.data),

    /** Poll a Drive graph upload task by its id. */
    getGraphUploadStatus: (
        operationId: number | string,
        taskId: string,
    ): Promise<GraphUploadStatus> =>
        ytgApi
            .get<GraphUploadStatus>(ytgEndpoints.uploadGraphsStatus(operationId, taskId))
            .then((r) => r.data),

    // ========== ADMIN ==========
    /** Admin: rename an operation. */
    renameOperation: (opId: number | string, name: string): Promise<unknown> =>
        ytgApi.patch<unknown>(ytgEndpoints.adminRename(opId), { name }).then((r) => r.data),

    /**
     * Change an operation's competitor `top` and recompute the stored averages.
     *
     * A purely local recomputation on the backend (re-averages already-stored SERP
     * scores, no external API call). Allowed once the competitor stage has completed.
     *
     * @param opId - The operation id.
     * @param top - The new number of top competitor positions (1–10).
     * @returns The dispatch result (message, operation_id, top, task_id).
     */
    recomputeCompetitors: (opId: number | string, top: number): Promise<unknown> =>
        ytgApi.post<unknown>(ytgEndpoints.recomputeCompetitors(opId), { top }).then((r) => r.data),

    /** Per-row task status grid for an operation (admin only). */
    getAdminRowsStatus: (
        opId: number | string,
        params?: AdminRowsStatusParams,
        config?: AxiosRequestConfig,
    ): Promise<AdminRowsStatusResponse> =>
        ytgApi
            .get<AdminRowsStatusResponse>(ytgEndpoints.adminRowsStatus(opId), { params, ...config })
            .then((r) => r.data),

    /** Per-row website-extraction trace + outcome summary for an operation (admin only). */
    getOperationExtraction: (
        opId: number | string,
        params?: OperationExtractionParams,
        config?: AxiosRequestConfig,
    ): Promise<OperationExtractionResponse> =>
        ytgApi
            .get<OperationExtractionResponse>(ytgEndpoints.adminOperationExtraction(opId), { params, ...config })
            .then((r) => r.data),

    /** Admin: override an operation's overall status to any ProcessStatus value. */
    setOperationStatus: (opId: number | string, status: ProcessStatusValue): Promise<unknown> =>
        ytgApi.post<unknown>(ytgEndpoints.adminSetOperationStatus(opId), { status }).then((r) => r.data),

    /** Admin: override a single task's status to any ProcessStatus value. */
    setTaskStatus: (
        opId: number | string, task: RestartableTask, status: ProcessStatusValue,
    ): Promise<unknown> =>
        ytgApi.post<unknown>(ytgEndpoints.adminSetTaskStatus(opId, task), { status }).then((r) => r.data),

    /**
     * Restart a single task on an arbitrary set of rows (admin only).
     *
     * `force` re-triggers the YTG generation even for guides already present
     * (bypasses the "guide already exists" skip). It is **only** valid for
     * `task = "gen_guide"`; the gateway returns 400 if sent with any other task.
     *
     * `continueChain` chains the subsequent enabled tasks of the operation after
     * this one completes, for the selected rows. It is valid for every task and
     * the response echoes the chained `next_tasks`.
     */
    restartTaskByRowIds: (
        opId: number | string,
        task: RestartableTask,
        rowIds: number[],
        force: boolean = false,
        continueChain: boolean = false,
        config?: AxiosRequestConfig,
    ): Promise<AdminRestartByRowIdsResponse> =>
        ytgApi
            .post<AdminRestartByRowIdsResponse>(
                ytgEndpoints.adminRestartByRowIds(opId, task),
                { row_ids: rowIds, force, continue_chain: continueChain },
                config,
            )
            .then((r) => r.data),
};
